<template>
    <card xs-tile sm-tile class="flex-grow-1">
        <card-text fill class="p-0">
            <div v-if="pageStyle && pageStyle.content" class="p-16 border-b">
                <user-content :text="pageStyle.content" />
            </div>

            <div class="flex items-center border-b p-16">
                <form-input name="search"
                            v-model="filterInputData.search"
                            hide-details
                            inverted
                            clearable
                            class="lg:min-w-500"
                            :icon="icons.search"
                            :placeholder="$t('placeholder.search')" @update:modelValue="handleSearchInput" @clear="handleSearchInput"/>

                <form-date-input v-model="filterInputData.min_date"
                                      class="ml-8 none sm:block"
                                      name="min_date"
                                      time
                                      hide-details
                                      inverted
                                      clearable
                                      :color="eventStore.colorStyle"
                                      :rules="{max: filterInputData.max_date}"
                                      :label="$t('label.min_date')" @blur="applyFilterInputs" @clear="applyFilterInputs"/>

                <form-date-input v-model="filterInputData.max_date"
                                      class="ml-8 none sm:block"
                                      name="max_date"
                                      time
                                      hide-details
                                      inverted
                                      clearable
                                      :color="eventStore.colorStyle"
                                      :rules="{min: filterInputData.min_date}"
                                      :label="$t('label.max_date')" @blur="applyFilterInputs" @clear="applyFilterInputs"/>

                <div class="flex">
                    <btn :color="filterExists ? eventStore.colorStyle : null" @click.stop="openFilterModal" class="sm:none ml-8">
                        <icon :icon="icons.extended"/>
                    </btn>
                    <btn v-if="filterExists" @click.prevent="resetFilter" class="ml-8">
                        <icon :icon="icons.reset"/>
                    </btn>
                </div>
            </div>

            <fetch-loader v-if="pending" :pending="pending" @retry="refresh" fill/>
            <template v-else-if="postingsData">

                <template v-if="postingsData.postings.length">
                    <job-list-item v-for="posting in postingsData.postings" :key="`posting-${posting.id}`" :job="posting" :to="$web('events.jobs.show', [eventStore.event._key, posting._key])"/>
                </template>

                <div class="px-16">
                    <expansion-panels v-if="postingsData.tags.length" v-model="openTag" accordion :class="{'border-t': postingsData.tags.length}">
                        <expansion-panel v-for="tag in postingsData.tags" :key="`tag-${tag.id}`" :value="tag._key" medium :title="tag.title" :subtitle="tag.description" class="py-8">
							<job-list-item v-for="posting in tag.postings" :key="`tag-${tag.id}-posting-${posting.id}`" :job="posting" :to="$web('events.jobs.show', [eventStore.event._key, posting._key])"/>
						</expansion-panel>
                    </expansion-panels>
                </div>

                <missing-info v-if="!postingsData.postings.length && !postingsData.tags.length" :text="filterExists ? $t('missing.search') : $t('missing.job.index')" class="flex-grow-1" no-spacing/>
            </template>
        </card-text>

        <client-only>
			<modal v-if="$breakpoint.xsOnly" v-model="showFilterModal" :title="$t('action.search_and_filter')">
				<form-input v-model="filterInputData.search"
							type="search"
							enterkeyhint="next"
							:label="$t('action.search')" clearable/>

				<form-date-input v-model="filterInputData.min_date"
									  name="min_date"
									  clearable
									  time
									  :color="eventStore.colorStyle"
									  :rules="{max: filterInputData.max_date}"
									  :label="$t('label.min_date')"/>

				<form-date-input v-model="filterInputData.max_date"
									  name="max_date"
									  clearable
									  time
									  :color="eventStore.colorStyle"
									  :rules="{min: filterInputData.min_date}"
									  :label="$t('label.max_date')"/>

				<template #button>
					<btn @click.prevent="applyFilterInputs" color="success">
						<icon :icon="icons.submit"/>
						<span>{{ $t('action.apply_filter') }}</span>
					</btn>
				</template>
			</modal>
        </client-only>

    </card>
</template>

<script setup lang="ts">
import {EventPageType} from "@spoferan/spoferan-ts-core";
import {mdiTune} from "@mdi/js";
import {deepEqual} from "@spoferan/spoferan-ts-core";
import {debounce} from "@spoferan/spoferan-ts-core";
import {submitIcon, searchIcon, resetIcon} from "@spoferan/nuxt-spoferan/icons";
import {useEventStore} from "../../../../store/event";

const eventStore = useEventStore();
const {t} = useI18n();
const {$api, $web, $apiFetch, $date, $breakpoint} = useNuxtApp();
const route = useRoute();
const router = useRouter();
const {updateUrlQuery} = useUrlMutations();

// Make sure the page is activated
const page = eventStore.getPage(EventPageType.Jobs)
if (!page) {
	await navigateTo($web('events.show', route.params.event));
}

const {data: pageStyle} = await useApiFetch($api('events.pages.show', [eventStore.event.id, page.id]), {
	guest: true,
	params: {
		ts: page.cache_buster,
	}
});

const filterData = ref({
	search: route.query.search,
	min_date: route.query.min_date,
	max_date: route.query.max_date,
});
const filterInputData = ref({...filterData.value});

const {reactiveData: postingsData, pending, refresh} = await useApiFetch(`/v1/views/events/${eventStore.event.id}/grouped-jobs`, {
	auth: true,
	params: filterData,
});

const openTag = ref(null);

watch(pending, (newValue) => {
	// If there is only one tag, open it
	if (!newValue && postingsData.value.tags?.length === 1) {
		openTag.value = postingsData.value.tags[0]._key;
	}
});

const showFilterModal = ref(false);

const icons = {
	submit: submitIcon,
	extended: mdiTune,
	search: searchIcon,
	reset: resetIcon,
};

const filterExists = computed(() => {
	return Object.values(filterData.value).filter(value => !!value).length > 0
});

function resetFilter() {
	filterInputData.value = {};
	applyFilterInputs();
}

function openFilterModal() {
	showFilterModal.value = true
}

function applyFilterInputs() {
	showFilterModal.value = false;

	if (!deepEqual(filterData.value, filterInputData.value)) {
		openTag.value = null;
		updateUrlQuery(filterInputData.value);
		filterData.value = {...filterInputData.value};
	}
}

const handleSearchInput = debounce(function () {
	if (!pending.value) {
		applyFilterInputs();
	}
}, 300)

useMeta({
	title: eventStore.buildPageTitle(t('view.events.jobs.index.title', {event: eventStore.event.display_name})),
	description: t('view.events.jobs.index.description', {event: eventStore.event.display_name}),
	canonical: $web('jobs.events.show', [eventStore.event._key], true)
})

</script>

